import React from "react";
import {
  MdDownload,
  MdEdit,
  MdPerson,
  MdSchool,
  MdCalendarToday,
  MdCheckCircle,
  MdEditNote,
  MdArticle,
  MdImage,
  MdZoomIn,
  MdClose,
  MdPictureAsPdf,
  MdTableChart,
  MdExpandMore,
  MdExpandLess,
} from "react-icons/md";
import * as XLSX from "xlsx";
import { reportService } from "services/admin/reportService";
import { authService } from "services/authService";
import {
  isImageFile,
  isPdfFile,
  isExcelFile,
  getFileTypeIcon,
} from "utils/fileHelpers";
import { useLanguage } from "context/LanguageContext";
import type { SessionReport } from "types/domain";

interface Props {
  sessionId: string;
  onEdit?: (report: SessionReport) => void;
  onReportsLoaded?: (reports: SessionReport[]) => void;
}

const REPORT_STATUS_KEY: Record<string, string> = {
  SUBMITTED: "enum.reportStatus.SUBMITTED",
  DRAFT: "enum.reportStatus.DRAFT",
};

const ROLE_KEY: Record<string, string> = {
  TRAINER: "enum.role.TRAINER",
  TEACHER: "enum.role.TEACHER",
  MANAGER: "enum.role.MANAGER",
};

const SESSION_TYPE_KEY: Record<string, string> = {
  TRAINING: "reportView.sessionType.TRAINING",
  CLASS: "reportView.sessionType.CLASS",
  OPEN_GYM: "reportView.sessionType.OPEN_GYM",
};

const FIELD_TYPE_KEY: Record<string, string> = {
  TEXT: "reportView.fieldType.TEXT",
  NUMBER: "reportView.fieldType.NUMBER",
  FILE: "reportView.fieldType.FILE",
  MULTIPLE_FILES: "reportView.fieldType.MULTIPLE_FILES",
};

/** Group a report's field_values by template_field_id, preserving original order. */
const groupFieldValues = (fieldValues: SessionReport["field_values"]) => {
  const order: string[] = [];
  const groups: Record<string, SessionReport["field_values"]> = {};
  for (const fv of fieldValues) {
    if (!groups[fv.template_field_id]) {
      groups[fv.template_field_id] = [];
      order.push(fv.template_field_id);
    }
    groups[fv.template_field_id].push(fv);
  }
  return order.map((templateFieldId) => groups[templateFieldId]);
};

const parseExcelFile = async (
  url: string
): Promise<Array<{ name: string; data: any[][] }>> => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("reportView.excelLoadFailed");

    const arrayBuffer = await response.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: "array" });

    const sheets = workbook.SheetNames.map((sheetName) => {
      const worksheet = workbook.Sheets[sheetName];
      const data = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: "",
      }) as any[][];
      return { name: sheetName, data };
    });

    return sheets;
  } catch (error) {
    throw new Error("reportView.excelReadFailed");
  }
};

const fmt = (iso: string | null | undefined, locale: string) =>
  iso
    ? new Date(iso).toLocaleString(locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

const ReportView: React.FC<Props> = ({
  sessionId,
  onEdit,
  onReportsLoaded,
}) => {
  const { t, locale } = useLanguage();
  const [reports, setReports] = React.useState<SessionReport[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [templateFilter, setTemplateFilter] = React.useState("ALL");
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set());
  const [lightboxImage, setLightboxImage] = React.useState<{
    url: string;
    name: string;
  } | null>(null);
  const [thumbnailUrls, setThumbnailUrls] = React.useState<
    Record<string, string>
  >({});
  const [pdfModal, setPdfModal] = React.useState<{
    url: string;
    name: string;
  } | null>(null);
  const [excelModal, setExcelModal] = React.useState<{
    fileName: string;
    sheets: Array<{ name: string; data: any[][] }>;
    activeSheetIndex: number;
  } | null>(null);
  const [excelLoading, setExcelLoading] = React.useState(false);
  const [previewError, setPreviewError] = React.useState<string | null>(null);
  const currentUser = authService.getStoredUser();

  React.useEffect(() => {
    const load = async () => {
      try {
        const data = await reportService.getForSession(sessionId);
        setReports(data);
        setExpandedIds(new Set());
        onReportsLoaded?.(data);

        // Fetch thumbnail URLs for all image files
        const urlCache: Record<string, string> = {};
        for (const report of data) {
          for (const fv of report.field_values) {
            if (
              (fv.field_type === "FILE" ||
                fv.field_type === "MULTIPLE_FILES") &&
              fv.file_key &&
              isImageFile(fv.file_name ?? "")
            ) {
              const key = `${report.id}_${fv.id}`;
              const valueId =
                fv.field_type === "MULTIPLE_FILES" ? fv.id : undefined;
              try {
                const result = await reportService.getThumbnailUrl(
                  report.id,
                  fv.template_field_id,
                  valueId
                );
                urlCache[key] = result.url;
              } catch (err) {
                // Silently fail - thumbnail will show as broken image
              }
            }
          }
        }
        setThumbnailUrls(urlCache);
      } catch (err: any) {
        setError(err.message ?? "reportView.loadFailed");
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [sessionId]);

  const handleDownload = async (
    reportId: string,
    fieldId: string,
    valueId?: string
  ) => {
    try {
      const result = await reportService.downloadUrl(
        reportId,
        fieldId,
        valueId
      );
      window.open(result.url, "_blank");
    } catch (err: any) {
      alert(err.message ?? t("reportView.downloadFailed"));
    }
  };

  const handleImagePreview = async (
    reportId: string,
    fieldId: string,
    fileName: string,
    valueId?: string
  ) => {
    try {
      const result = await reportService.getThumbnailUrl(
        reportId,
        fieldId,
        valueId
      );
      setLightboxImage({ url: result.url, name: fileName });
    } catch (err: any) {
      alert(err.message ?? t("reportView.imageLoadFailed"));
    }
  };

  const handlePdfPreview = async (
    reportId: string,
    fieldId: string,
    fileName: string,
    valueId?: string
  ) => {
    try {
      const result = await reportService.downloadUrl(
        reportId,
        fieldId,
        valueId
      );
      setPdfModal({ url: result.url, name: fileName });
    } catch (err: any) {
      setPreviewError(err.message ?? "reportView.pdfLoadFailed");
    }
  };

  const handleExcelPreview = async (
    reportId: string,
    fieldId: string,
    fileName: string,
    valueId?: string
  ) => {
    setExcelLoading(true);
    setPreviewError(null);
    try {
      const result = await reportService.downloadUrl(
        reportId,
        fieldId,
        valueId
      );
      const sheets = await parseExcelFile(result.url);

      if (sheets.length === 0) {
        throw new Error("reportView.excelEmpty");
      }

      setExcelModal({ fileName, sheets, activeSheetIndex: 0 });
    } catch (err: any) {
      setPreviewError(err.message ?? "reportView.excelLoadFailed");
    } finally {
      setExcelLoading(false);
    }
  };

  const renderFilePreview = (
    reportId: string,
    fv: SessionReport["field_values"][number],
    valueId?: string
  ) => {
    if (!fv.file_key) return null;
    const fileType = getFileTypeIcon(fv.file_name ?? "");

    // IMAGE FILES (existing implementation)
    if (fileType === "image") {
      return (
        <div className="flex items-start gap-3">
          <button
            onClick={() =>
              handleImagePreview(
                reportId,
                fv.template_field_id,
                fv.file_name ?? t("reportView.image"),
                valueId
              )
            }
            className="group relative h-24 w-24 shrink-0 overflow-hidden rounded-lg border-2 border-line transition-all hover:border-brand-500 hover:shadow-md dark:hover:border-brand-400"
            title={t("reportView.enlargeImage")}
          >
            <img
              src={thumbnailUrls[`${reportId}_${fv.id}`] ?? ""}
              alt={fv.file_name ?? t("reportView.image")}
              className="h-full w-full object-cover"
              loading="lazy"
            />
            <div className="bg-black/0 group-hover:bg-black/40 absolute inset-0 flex items-center justify-center transition-all">
              <MdZoomIn className="h-6 w-6 text-white opacity-0 transition-opacity group-hover:opacity-100" />
            </div>
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <MdImage className="h-4 w-4 text-ink-subtle" />
              <span className="text-sm font-medium text-ink-muted">
                {fv.file_name ?? t("reportView.image")}
              </span>
            </div>
            <button
              onClick={() =>
                handleDownload(reportId, fv.template_field_id, valueId)
              }
              className="mt-1 flex items-center gap-1 text-xs text-brand-500 hover:underline"
            >
              <MdDownload className="h-3.5 w-3.5" />
              {t("reportView.download")}
            </button>
          </div>
        </div>
      );
    }

    // PDF FILES
    if (fileType === "pdf") {
      return (
        <div className="flex items-center gap-3 rounded-lg border border-line bg-gray-50 p-3 dark:bg-surface-muted/50">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/30">
            <MdPictureAsPdf className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-medium text-ink-muted">
                {fv.file_name ?? t("reportView.pdfDocument")}
              </span>
              <span className="shrink-0 rounded bg-red-100 px-2 py-0.5 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-400">
                PDF
              </span>
            </div>
            <div className="mt-1 flex items-center gap-2">
              <button
                onClick={() =>
                  handlePdfPreview(
                    reportId,
                    fv.template_field_id,
                    fv.file_name ?? t("reportView.pdfDocument"),
                    valueId
                  )
                }
                className="flex items-center gap-1 text-xs text-brand-500 hover:underline"
              >
                <MdZoomIn className="h-3.5 w-3.5" />
                {t("reportView.viewPdf")}
              </button>
              <span className="text-gray-300">•</span>
              <button
                onClick={() =>
                  handleDownload(reportId, fv.template_field_id, valueId)
                }
                className="flex items-center gap-1 text-xs text-brand-500 hover:underline"
              >
                <MdDownload className="h-3.5 w-3.5" />
                {t("reportView.download")}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // EXCEL FILES
    if (fileType === "excel") {
      return (
        <div className="flex items-center gap-3 rounded-lg border border-line bg-gray-50 p-3 dark:bg-surface-muted/50">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/30">
            <MdTableChart className="h-6 w-6 text-green-600 dark:text-green-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-medium text-ink-muted">
                {fv.file_name ?? t("reportView.excelSpreadsheet")}
              </span>
              <span className="shrink-0 rounded bg-green-100 px-2 py-0.5 text-xs text-green-700 dark:bg-green-900/30 dark:text-green-400">
                Excel
              </span>
            </div>
            <div className="mt-1 flex items-center gap-2">
              <button
                onClick={() =>
                  handleExcelPreview(
                    reportId,
                    fv.template_field_id,
                    fv.file_name ?? t("reportView.excelSpreadsheet"),
                    valueId
                  )
                }
                className="flex items-center gap-1 text-xs text-brand-500 hover:underline"
              >
                <MdTableChart className="h-3.5 w-3.5" />
                {t("reportView.viewSpreadsheet")}
              </button>
              <span className="text-gray-300">•</span>
              <button
                onClick={() =>
                  handleDownload(reportId, fv.template_field_id, valueId)
                }
                className="flex items-center gap-1 text-xs text-brand-500 hover:underline"
              >
                <MdDownload className="h-3.5 w-3.5" />
                {t("reportView.download")}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // OTHER FILES (fallback)
    return (
      <button
        onClick={() => handleDownload(reportId, fv.template_field_id, valueId)}
        className="flex items-center gap-1.5 font-medium text-brand-500 hover:underline"
      >
        <MdDownload className="h-4 w-4" />
        {fv.file_name ?? t("reportView.downloadFile")}
      </button>
    );
  };

  const templateOptions = React.useMemo(() => {
    const entries = reports.map((report) => ({
      value: report.template_id ?? "NO_TEMPLATE",
      label: report.template?.name ?? t("reportView.noTemplate"),
    }));

    const map = new Map<string, string>();
    entries.forEach((entry) => {
      if (!map.has(entry.value)) {
        map.set(entry.value, entry.label);
      }
    });

    return [
      { value: "ALL", label: t("reportView.allTemplates") },
      ...Array.from(map.entries()).map(([value, label]) => ({ value, label })),
    ];
  }, [reports, t]);

  const filteredReports = React.useMemo(() => {
    if (templateFilter === "ALL") return reports;
    return reports.filter(
      (report) => (report.template_id ?? "NO_TEMPLATE") === templateFilter
    );
  }, [reports, templateFilter]);

  const hasTypeFilter = templateFilter !== "ALL";

  const toggleExpanded = (reportId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(reportId)) {
        next.delete(reportId);
      } else {
        next.add(reportId);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-4 border-brand-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-16 text-red-500">
        <span className="text-sm">{t(error)}</span>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-gray-50 px-4 py-3 dark:bg-surface/50">
        <div>
          <p className="text-sm font-semibold text-ink">
            {t("reportView.filterTitle")}
          </p>
          <p className="mt-0.5 text-xs text-ink-muted">
            {t("reportView.filterHint")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={templateFilter}
            onChange={(event) => setTemplateFilter(event.target.value)}
            className="min-w-[220px] rounded-lg border border-line bg-surface px-3 py-2 text-sm outline-none dark:text-white"
          >
            {templateOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {hasTypeFilter ? (
            <button
              type="button"
              onClick={() => setTemplateFilter("ALL")}
              className="rounded-lg border border-line px-3 py-2 text-sm font-medium text-gray-600 hover:bg-white dark:text-gray-300 dark:hover:bg-surface-hover"
            >
              {t("action.clearFilters")}
            </button>
          ) : null}
        </div>
      </div>

      {filteredReports.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-ink-subtle">
          <MdArticle className="h-12 w-12 opacity-30" />
          <p className="text-sm">
            {hasTypeFilter
              ? t("reportView.emptyFiltered")
              : t("reportView.empty")}
          </p>
        </div>
      ) : null}

      {filteredReports.map((report) => {
        const isSubmitted = report.status === "SUBMITTED";
        const canEdit =
          onEdit && currentUser && report.trainer_id === currentUser.id;
        const isExpanded = expandedIds.has(report.id);

        return (
          <div
            key={report.id}
            className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm"
          >
            {/* ── Coloured top strip ── */}
            <div
              className={`h-1.5 w-full ${
                isSubmitted
                  ? "to-emerald-500 bg-gradient-to-r from-green-400"
                  : "bg-gradient-to-r from-yellow-400 to-amber-400"
              }`}
            />

            <div className="p-5">
              {/* ── Header (click to expand/collapse) ── */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => toggleExpanded(report.id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    toggleExpanded(report.id);
                  }
                }}
                className={`flex cursor-pointer flex-wrap items-start justify-between gap-3 ${
                  isExpanded ? "mb-4" : ""
                }`}
              >
                {/* Left: trainer + session meta */}
                <div className="flex items-start gap-3">
                  {/* Avatar circle */}
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-50 dark:bg-surface-muted">
                    <MdPerson className="h-6 w-6 text-brand-500" />
                  </div>

                  <div className="space-y-1">
                    {/* Trainer name + role */}
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-base font-bold text-ink">
                        {report.trainer?.full_name ?? t("enum.role.TRAINER")}
                      </span>
                      {report.trainer?.role && (
                        <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-600 dark:bg-surface-muted dark:text-brand-400">
                          {ROLE_KEY[report.trainer.role]
                            ? t(ROLE_KEY[report.trainer.role])
                            : report.trainer.role}
                        </span>
                      )}
                    </div>

                    {/* Session info row */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
                      {report.session && (
                        <>
                          <span className="flex items-center gap-1">
                            <MdSchool className="h-3.5 w-3.5" />
                            {report.session.title}
                            {report.session.class_ && (
                              <span className="text-ink-subtle">
                                &nbsp;·&nbsp;{report.session.class_.name}
                              </span>
                            )}
                          </span>
                          <span className="text-gray-300 dark:text-navy-600">
                            |
                          </span>
                          <span className="rounded bg-surface-muted px-1.5 py-0.5 text-ink-muted">
                            {SESSION_TYPE_KEY[report.session.session_type]
                              ? t(SESSION_TYPE_KEY[report.session.session_type])
                              : report.session.session_type}
                          </span>
                          {report.session.start_time && (
                            <>
                              <span className="text-gray-300 dark:text-navy-600">
                                |
                              </span>
                              <span className="flex items-center gap-1">
                                <MdCalendarToday className="h-3.5 w-3.5" />
                                {fmt(report.session.start_time, locale)}
                              </span>
                            </>
                          )}
                        </>
                      )}
                    </div>

                    {/* Template */}
                    {report.template && (
                      <div className="flex items-center gap-1 text-xs text-ink-subtle">
                        <MdEditNote className="h-3.5 w-3.5" />
                        {t("reportView.templateLabel", {
                          name: report.template.name,
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: badges + edit */}
                <div className="flex flex-wrap items-center gap-2">
                  {/* Status badge */}
                  <span
                    className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${
                      isSubmitted
                        ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                        : "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
                    }`}
                  >
                    <MdCheckCircle className="h-3.5 w-3.5" />
                    {t(REPORT_STATUS_KEY[isSubmitted ? "SUBMITTED" : "DRAFT"])}
                  </span>

                  {/* Submitted date */}
                  <span className="text-xs text-ink-subtle">
                    {fmt(report.created_at, locale)}
                  </span>

                  {/* Edit button */}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        onEdit(report);
                      }}
                      className="flex items-center gap-1.5 rounded-lg border border-brand-500 px-3 py-1 text-xs font-medium text-brand-500 transition-colors hover:bg-brand-50 dark:hover:bg-brand-900/10"
                    >
                      <MdEdit className="h-3.5 w-3.5" />
                      {t("action.edit")}
                    </button>
                  )}

                  {/* Expand/collapse chevron */}
                  {isExpanded ? (
                    <MdExpandLess className="h-5 w-5 text-ink-subtle" />
                  ) : (
                    <MdExpandMore className="h-5 w-5 text-ink-subtle" />
                  )}
                </div>
              </div>

              {isExpanded && (
                <>
                  {/* ── Divider ── */}
                  <div className="mb-4 h-px bg-surface-muted" />

                  {/* ── Content / notes ── */}
                  {report.content && (
                    <div className="mb-4 rounded-xl bg-gray-50 px-4 py-3 dark:bg-surface-muted/50">
                      <p className="text-sm leading-relaxed text-ink-muted">
                        {report.content}
                      </p>
                    </div>
                  )}

                  {/* ── Field values ── */}
                  {report.field_values.length > 0 &&
                    (() => {
                      const groups = groupFieldValues(report.field_values);
                      return (
                        <div className="overflow-hidden rounded-xl border border-line">
                          {groups.map((group, idx) => {
                            const fv = group[0];
                            const isMultipleFiles =
                              fv.field_type === "MULTIPLE_FILES";
                            return (
                              <div
                                key={fv.template_field_id}
                                className={`flex items-start gap-4 px-4 py-3 ${
                                  idx !== groups.length - 1
                                    ? "border-b border-line"
                                    : ""
                                }`}
                              >
                                {/* Label + type pill */}
                                <div className="w-40 shrink-0">
                                  <p className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                                    {fv.field_label ?? t("reportView.field")}
                                  </p>
                                  {fv.field_type && (
                                    <span className="mt-0.5 inline-block rounded bg-surface-muted px-1.5 py-0.5 text-[10px] text-ink-subtle">
                                      {FIELD_TYPE_KEY[fv.field_type]
                                        ? t(FIELD_TYPE_KEY[fv.field_type])
                                        : fv.field_type}
                                    </span>
                                  )}
                                </div>

                                {/* Value */}
                                <div className="flex-1 text-sm text-ink">
                                  {isMultipleFiles ? (
                                    group.some((item) => item.file_key) ? (
                                      <div className="space-y-3">
                                        {group.map((item) => (
                                          <div key={item.id}>
                                            {renderFilePreview(
                                              report.id,
                                              item,
                                              item.id
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <span>—</span>
                                    )
                                  ) : fv.field_type === "FILE" &&
                                    fv.file_key ? (
                                    renderFilePreview(report.id, fv)
                                  ) : fv.field_type === "NUMBER" ? (
                                    <span className="font-medium">
                                      {fv.number_value ?? "—"}
                                    </span>
                                  ) : (
                                    <span className="whitespace-pre-wrap leading-relaxed">
                                      {fv.text_value || "—"}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                </>
              )}
            </div>
          </div>
        );
      })}

      {/* Image Lightbox Modal */}
      {lightboxImage && (
        <div
          className="bg-black/90 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
            aria-label={t("action.close")}
          >
            <MdClose className="h-6 w-6" />
          </button>
          <div
            className="relative max-h-[90vh] max-w-[90vw]"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxImage.url}
              alt={lightboxImage.name}
              className="max-h-[90vh] max-w-[90vw] rounded-lg shadow-2xl"
            />
            <div className="mt-3 flex items-center justify-center gap-3 text-white">
              <MdImage className="h-5 w-5" />
              <p className="text-sm font-medium">{lightboxImage.name}</p>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      {pdfModal && (
        <div
          className="bg-black/90 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setPdfModal(null)}
        >
          <button
            onClick={() => setPdfModal(null)}
            className="absolute right-4 top-4 z-10 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
            aria-label={t("action.close")}
          >
            <MdClose className="h-6 w-6" />
          </button>

          <div
            className="relative flex h-[90vh] w-full max-w-6xl flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between rounded-t-lg bg-white/10 px-4 py-3 backdrop-blur-sm">
              <div className="flex items-center gap-3 text-white">
                <MdPictureAsPdf className="h-5 w-5" />
                <p className="text-sm font-medium">{pdfModal.name}</p>
              </div>
              <a
                href={pdfModal.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-lg bg-white/20 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-white/30"
              >
                <MdDownload className="h-4 w-4" />
                {t("reportView.download")}
              </a>
            </div>

            <div className="flex-1 overflow-hidden rounded-lg bg-white shadow-2xl">
              <iframe
                src={pdfModal.url}
                className="h-full w-full"
                title={pdfModal.name}
              />
            </div>
          </div>
        </div>
      )}

      {/* Excel Preview Modal */}
      {excelModal && (
        <div
          className="bg-black/90 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setExcelModal(null)}
        >
          <button
            onClick={() => setExcelModal(null)}
            className="absolute right-4 top-4 z-10 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
            aria-label={t("action.close")}
          >
            <MdClose className="h-6 w-6" />
          </button>

          <div
            className="relative flex h-[90vh] w-full max-w-6xl flex-col rounded-lg bg-surface shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-line px-6 py-4">
              <div className="flex items-center gap-3">
                <MdTableChart className="h-5 w-5 text-green-600 dark:text-green-400" />
                <h3 className="text-lg font-semibold text-ink">
                  {excelModal.fileName}
                </h3>
                <span className="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                  {t("reportView.sheetSize", {
                    rows:
                      excelModal.sheets[excelModal.activeSheetIndex]?.data
                        .length ?? 0,
                    cols:
                      excelModal.sheets[excelModal.activeSheetIndex]?.data[0]
                        ?.length ?? 0,
                  })}
                </span>
              </div>
              <button
                onClick={() => setExcelModal(null)}
                className="rounded-lg p-1 text-ink-subtle hover:bg-surface-hover"
              >
                <MdClose className="h-5 w-5" />
              </button>
            </div>

            {/* Sheet Tabs (if multiple sheets) */}
            {excelModal.sheets.length > 1 && (
              <div className="flex gap-2 border-b border-line px-6 py-2">
                {excelModal.sheets.map((sheet, index) => (
                  <button
                    key={index}
                    onClick={() =>
                      setExcelModal({ ...excelModal, activeSheetIndex: index })
                    }
                    className={`rounded-t-lg px-4 py-2 text-sm font-medium transition-colors ${
                      index === excelModal.activeSheetIndex
                        ? "bg-brand-500 text-white"
                        : "bg-surface-muted text-gray-600 hover:bg-surface-hover dark:text-gray-300"
                    }`}
                  >
                    {sheet.name}
                  </button>
                ))}
              </div>
            )}

            {/* Table Content */}
            <div className="flex-1 overflow-auto p-6">
              {(() => {
                const sheet = excelModal.sheets[excelModal.activeSheetIndex];
                if (!sheet || sheet.data.length === 0) {
                  return (
                    <div className="flex h-full items-center justify-center text-ink-subtle">
                      <p className="text-sm">{t("reportView.sheetEmpty")}</p>
                    </div>
                  );
                }

                return (
                  <table className="min-w-full border-collapse text-sm">
                    <tbody>
                      {sheet.data.map((row, rowIndex) => (
                        <tr
                          key={rowIndex}
                          className={
                            rowIndex === 0
                              ? "bg-surface-muted font-semibold"
                              : ""
                          }
                        >
                          {row.map((cell, cellIndex) => (
                            <td
                              key={cellIndex}
                              className="border border-line px-3 py-2"
                            >
                              {cell ?? ""}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 border-t border-line px-6 py-4">
              <button
                onClick={() => setExcelModal(null)}
                className="rounded-lg border border-line px-4 py-2 text-sm font-medium text-gray-600 hover:bg-surface-hover dark:text-gray-300"
              >
                {t("action.close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Excel Loading Modal */}
      {excelLoading && (
        <div className="bg-black/40 fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
          <div className="rounded-2xl bg-surface px-6 py-4 shadow-2xl">
            <div className="flex items-center gap-3">
              <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-4 border-brand-500" />
              <p className="text-sm text-ink">
                {t("reportView.excelLoading")}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Toast */}
      {previewError && (
        <div className="fixed bottom-4 right-4 z-50 rounded-lg bg-red-500 px-4 py-3 text-white shadow-lg">
          <div className="flex items-center gap-2">
            <p className="text-sm">{t(previewError)}</p>

            <button
              onClick={() => setPreviewError(null)}
              className="ml-2 rounded p-1 hover:bg-red-600"
            >
              <MdClose className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportView;
